import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowBack } from "@mui/icons-material";
import type { GenericModalProps } from "./types";
import InfoTooltip from "../InfoTooltip/InfoTooltip";
import ModalCloseButton from "../ModalCloseButton/ModalCloseButton";
import {
	MODAL_BODY_DEFAULT_PADDING,
	MODAL_BODY_WITH_FOOTER_PADDING,
	MODAL_BORDER_RADIUS_CLASS,
	MODAL_FOOTER_PADDING,
	MODAL_HEADER_BACK_BUTTON_CLASS,
	MODAL_TITLE_CLASS,
	isStandardModalPadding,
	modalHeaderClassName,
} from "@config/theme/modals";

const Modal: React.FC<GenericModalProps> = ({
	isOpen,
	onClose,
	title,
	icon,
	showCloseIcon = true,
	children,
	footer,
	width = "max-w-md",
	height = "auto",
	backgroundColor = "bg-white",
	rounded = MODAL_BORDER_RADIUS_CLASS,
	padding = MODAL_BODY_DEFAULT_PADDING,
	showBackIcon,
	onBack,
	className = "",
	isTooltipEnabled,
	tooltipText,
	headerImageSrc,
	headerImageAlt,
	titleClassName = MODAL_TITLE_CLASS,
	headerClassName = "",
	zIndex = "z-50",
}) => {
	const getResponsiveWidth = () => {
		if (width === "max-w-md" || width === "max-w-sm" || width === "max-w-lg") {
			return width;
		}
		if (width === "max-w-4xl" || width === "max-w-5xl" || width === "max-w-6xl") {
			return width;
		}
		return width;
	};

	const responsiveWidth = getResponsiveWidth();
	const isZeroPadding = padding === "p-0" || padding === "0";
	const usesSplitFooterPadding = Boolean(footer) && !isZeroPadding && isStandardModalPadding(padding);
	const bodyPadding = isZeroPadding
		? ""
		: usesSplitFooterPadding
			? MODAL_BODY_WITH_FOOTER_PADDING
			: padding;
	const footerPadding = usesSplitFooterPadding ? MODAL_FOOTER_PADDING : isZeroPadding ? "" : bodyPadding;
	const showHeader = Boolean(title || icon || showBackIcon || showCloseIcon);
	const shellHeightClass = height === "auto" ? "max-h-[calc(100dvh-2rem)]" : height;
	const resolvedHeaderClassName = headerClassName?.trim() ? headerClassName : modalHeaderClassName;

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = "hidden";
			document.documentElement.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
			document.documentElement.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
			document.documentElement.style.overflow = "unset";
		};
	}, [isOpen]);

	if (!isOpen) return null;

	const modalContent = (
		<div
			className={`fixed inset-0 ${zIndex} flex h-dvh min-h-0 items-center justify-center overflow-hidden bg-black bg-opacity-30 p-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-4 sm:p-4`}
			role="dialog"
			style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
			<div
				className={`flex w-full min-h-0 flex-col overflow-hidden ${responsiveWidth} ${shellHeightClass} ${backgroundColor} ${rounded} relative shadow-xl ${className}`}>
				{showHeader && (
					<div className={resolvedHeaderClassName}>
						<div className="flex min-w-0 flex-1 items-center gap-2 [&_.MuiSvgIcon-root]:text-primary-foreground">
							{showBackIcon && (
								<button type="button" onClick={onBack} className={MODAL_HEADER_BACK_BUTTON_CLASS} aria-label="Go back">
									<ArrowBack className="h-5 w-5" />
								</button>
							)}
							{icon && <div className="shrink-0 text-primary-foreground">{icon}</div>}
							{title &&
								(typeof title === "string" ? (
									<h2 className={`min-w-0 ${titleClassName}`}>{title}</h2>
								) : (
									<div className={`min-w-0 ${titleClassName}`}>{title}</div>
								))}
							{isTooltipEnabled ? <InfoTooltip tooltip={tooltipText} /> : null}
						</div>
						{headerImageSrc ? (
							<div className="hidden shrink-0 items-center justify-center px-1 sm:flex">
								<img
									src={headerImageSrc}
									alt={headerImageAlt ?? ""}
									className="block max-h-12 max-w-[120px] object-contain"
								/>
							</div>
						) : null}
						{showCloseIcon ? <ModalCloseButton onClick={onClose} /> : null}
					</div>
				)}

				<div className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain ${bodyPadding}`.trim()}>
					{children}
				</div>

				{footer && <div className={`flex-shrink-0 ${footerPadding}`.trim()}>{footer}</div>}
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
};

export default Modal;
